import { UsageTrackerEvent, usageTracker } from "@volumental/analytic";
import giftCard from "@volumental/assets/images/Feedback/GiftCard.png";
import { Button, ErrorText, Headline, isValidEmail, Text, TextButton } from "@volumental/common";
import { useState } from "react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";
import type { SizeLocale } from "../../../api/gql/graphql";
import { Input } from "../../../components/accessible-components";
import { Modal } from "../../../components/modal/modal";
import { useScannerStore, useSessionStore } from "../../../store";

interface EmailCollectorProps {
  show: boolean;
  onClose: (feedback: boolean) => void;
  pdpDetail?: { local: SizeLocale; gender?: string; recommendedSize: string };
  scanID?: string;
  previousPurchaseID?: string;
}

export function EmailCollector({
  show,
  onClose,
  pdpDetail,
  scanID,
  previousPurchaseID,
}: EmailCollectorProps): JSX.Element {
  const { mode, productID } = useSessionStore((store) => store);
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const { flowType } = useScannerStore((store) => store);
  const [emailError, setEmailError] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  const handleEmailChange = (event: string): void => {
    if (event.length === 0) {
      setEmailError(false);
    } else {
      setEmailError(!isValidEmail(event.toLowerCase()));
    }
    setEmail(event.toLowerCase());
  };

  const uploadFeeback = (): void => {
    // uploadFeedback
    const feedback: Record<string, string | undefined> = {
      Email: email,
      recommended_size: pdpDetail?.recommendedSize,
      productID: productID ?? "UNKNOWN",
      size_local: pdpDetail?.local,
      product_gender: pdpDetail?.gender,
      scanID: scanID ?? "",
      previous_purchase_id: previousPurchaseID,
      sizing_flow_type: flowType,
      experience: mode,
    };
    usageTracker.track(UsageTrackerEvent.EmailCollectorWithGift, feedback);

    onClose(true);
  };

  const submitEmail = (): void => {
    setEmailError(!isValidEmail(email));
    if (isValidEmail(email)) {
      setError(false);
      uploadFeeback();
    }
  };

  const onSkip = (): void => {
    onClose(false);
  };

  return (
    <Modal show={show} close={onSkip} modalTitle={t("emailCollectorFeedback.titleWithGift")}>
      <Headline className="mb-2">{t("emailCollectorFeedback.titleWithGift")}</Headline>
      <Text>{t("emailCollectorFeedback.descriptionWithGift")}</Text>
      <div className="w-full flex flex-row justify-center mt-6 mb-2.5">
        <img className="rounded-xl w-full h-full object-contain" src={giftCard} alt={t("imageAlt.giftCard")} />
      </div>
      {<Text className="text-xs">{t("emailCollectorFeedback.imageDescriptionWithGift")}</Text>}
      <div className="pt-6">
        <Input
          value={email}
          onChange={handleEmailChange}
          error={emailError}
          type="email"
          crossClick={() => {
            setEmail("");
            setEmailError(false);
          }}
          placeholder={t("emailCollectorFeedback.emailPlaceholder")}
          applyBackground
          boarderOnFocus
          ariaLabel={t("ariaLabels.emailInput")}
        />
        {error && <ErrorText>{t("SomethingWentWrong")}</ErrorText>}
      </div>
      <div className="mt-4 mb-5 flex flex-col justify-center">
        <Button type="submit" disabled={!isValidEmail(email)} onClick={submitEmail}>
          {t("button.signUp")}
        </Button>
        <TextButton onClick={onSkip}>{t("button.skip")}</TextButton>
      </div>
    </Modal>
  );
}
